import { environment } from '#environments';
import { PagedList, StartWorkDoneResponse, Item, BulkCompleteReviewRequestDto } from '#models';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, Observable, of, share, throwError } from 'rxjs';
import { StartWorkDone } from 'src/models/start-work-done';
import { StartWorkDoneCreate } from 'src/models/start-work-done-create';
import { StartWorkDoneSummary } from 'src/models/start-work-done-summary';

export interface StartWorkDoneUpdatePayload {
  subAreaId: number;
  userEmail?: string;
  observationDate?: Date | null;
  categoryId?: number;
}

@Injectable({
  providedIn: 'root',
})
export class StartWorkDoneService {
  _httpClient = inject(HttpClient);

  private get _baseUrl(): string {
    return `${environment.apiUrl}/startworkdones`;
  }

  getList(params: {
    businessUnitId?: number;
    areaId?: number;
    subAreaId?: number;
    userEmail?: string;
  }): Observable<StartWorkDoneSummary[]> {
    const query = new URLSearchParams();
    if (params.businessUnitId) query.set('businessUnitId', String(params.businessUnitId));
    if (params.areaId) query.set('areaId', String(params.areaId));
    if (params.subAreaId) query.set('subAreaId', String(params.subAreaId));
    if (params.userEmail) query.set('userEmail', params.userEmail);
    const qs = query.toString();
    return this._httpClient.get<StartWorkDoneSummary[]>(`${this._baseUrl}${qs ? '?' + qs : ''}`);
  }

  getPagedList(params: {
    businessUnitId?: number;
    areaId?: number;
    subAreaId?: number;
    categoryIds?: number[];
    userEmail?: string;
    pageNumber: number;
    pageSize: number;
  }): Observable<PagedList<StartWorkDoneSummary>> {
    return this._httpClient.post<PagedList<StartWorkDoneSummary>>(`${this._baseUrl}/paged-list`, {
      filter: {
        businessUnitId: params.businessUnitId ?? null,
        areaId: params.areaId ?? null,
        subAreaId: params.subAreaId ?? null,
        categoryIds: params.categoryIds?.length ? params.categoryIds : null,
        userEmail: params.userEmail ?? null,
      },
      pagination: {
        pageNumber: params.pageNumber,
        pageSize: params.pageSize,
      },
    });
  }

  getById(id: number): Observable<StartWorkDone | null> {
    return this._httpClient.get<StartWorkDone>(`${this._baseUrl}/${id}`).pipe(share());
  }

  getStartWorkDone(subAreaId: number): Observable<StartWorkDone | null> {
    const result = this._httpClient.get<StartWorkDone>(`${this._baseUrl}?subAreaId=${subAreaId}`).pipe(
      share(),
      catchError(err => {
        if (err instanceof HttpErrorResponse && err.status === 404) {
          return of(null);
        }

        return throwError(() => err);
      })
    );

    return result;
  }

  createStartWorkDone(payload: StartWorkDoneCreate): Observable<StartWorkDone> {
    return this._httpClient.post<StartWorkDone>(this._baseUrl, payload);
  }

  updateStartWorkDone(id: number, payload: StartWorkDoneUpdatePayload): Observable<StartWorkDone> {
    return this._httpClient.put<StartWorkDone>(`${this._baseUrl}/${id}`, payload);
  }

  createResponse(response: StartWorkDoneResponse): Observable<StartWorkDoneResponse | null> {
    return this._httpClient.post<StartWorkDoneResponse>(`${environment.apiUrl}/responses`, response);
  }

  deleteResponse(item: Item): Observable<StartWorkDoneResponse | null> {
    return this._httpClient.delete<StartWorkDoneResponse>(`${environment.apiUrl}/responses/` + item.id);
  }

  updateResponse(item: Pick<Item, 'id' | 'answerId'> & { comment?: string }): Observable<StartWorkDoneResponse | null> {
    const payload = { id: item.id, answerId: item.answerId, comment: item.comment };

    return this._httpClient.put<StartWorkDoneResponse>(`${environment.apiUrl}/responses/${item.id}/answer`, payload);
  }

  updateResponseName(id: number, name: string): Observable<StartWorkDoneResponse | null> {
    return this._httpClient.put<StartWorkDoneResponse>(`${environment.apiUrl}/responses/${id}/name`, { id, name });
  }

  getReport(reviewId: number): Observable<Blob> {
    return this._httpClient.get<Blob>(`${this._baseUrl}/${reviewId}/report`, {
      responseType: 'blob' as 'json',
    });
  }

  deleteStartWorkDone(id: number): Observable<void> {
    return this._httpClient.delete<void>(`${this._baseUrl}/${id}`);
  }

  completeReview(reviewId: number): Observable<StartWorkDone | null> {
    const result = this._httpClient.put<StartWorkDone>(`${this._baseUrl}/${reviewId}/complete`, {}).pipe(
      share(),
      catchError(err => {
        if (err instanceof HttpErrorResponse && err.status === 404) {
          return of(null);
        }

        return throwError(() => err);
      })
    );

    return result;
  }

  sendEmail(id: number): Observable<void> {
    return this._httpClient.post<void>(`${this._baseUrl}/${id}/email`, {});
  }

  bulkCompleteReview(payload: BulkCompleteReviewRequestDto): Observable<void> {
    return this._httpClient.put<void>(`${this._baseUrl}/bulk-complete`, payload);
  }
}
